/**
 * Config for the `mcp` workflow node type.
 *
 * Targets an MCP-server application (an org property with `type: 'mcp'`) and
 * carries plain-language instructions an agent executes against the server's
 * tools. Action-style: cache replay first, agent fallback (execution lands in
 * later stack members — see the native MCP testing stack).
 *
 * The node references the application by **propertyId** and its auth by a
 * persona (`credentialTemplateId`, preferred) or a direct credential pin
 * (`credentialId`, escape hatch). MCP applications reuse the `api_key`
 * credential provider, whose key field is the bearer token.
 */
import { z } from 'zod';
import type { PlaybookStep } from './index.js';

export interface McpNodeConfig {
  /** MCP application (org property with type 'mcp') this node drives. */
  propertyId: string;
  /** Persona (credential template) supplying auth; preferred over credentialId. */
  credentialTemplateId?: string;
  /** Direct credential pin (escape hatch). */
  credentialId?: string;
  /** Plain-language goal the agent accomplishes via the server's tools. */
  instructions: string;
  /** Variable names this node must produce (→ $var.StepN_<label>.<name>). */
  declaredOutputs?: string[];
  /** Authored/minted playbook steps (same shape as action nodes). */
  playbookSteps?: PlaybookStep[];
  /** Skip cache replay and always run the agent. */
  alwaysUseAgent?: boolean;
  /** Restrict the tools exposed to the agent (names as advertised by the server). */
  toolAllowlist?: string[];
}

/**
 * Zod schema for {@link McpNodeConfig}. `playbookSteps` entries are accepted
 * structurally (`z.custom`) — the persisted {@link PlaybookStep} shape has no
 * standalone zod schema (only the agent-input subset does, see
 * `buildPlaybookStepAgentSchema`), and this schema must not reject persisted
 * runtime-attached fields.
 */
export const McpNodeConfigSchema = z.object({
  propertyId: z.string().min(1),
  credentialTemplateId: z.string().optional(),
  credentialId: z.string().optional(),
  instructions: z.string().min(1),
  declaredOutputs: z.array(z.string()).optional(),
  playbookSteps: z
    .array(z.custom<PlaybookStep>((v) => typeof v === 'object' && v !== null))
    .optional(),
  alwaysUseAgent: z.boolean().optional(),
  toolAllowlist: z.array(z.string()).optional(),
}) satisfies z.ZodType<McpNodeConfig>;
